import React, { useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { IconButton } from 'components/buttons/IconButton';
import { SearchField } from 'components/inputs/SearchField';
import { Callout } from 'components/layout/Callout';
import { ActionLink } from 'components/ActionLinks/ActionLink';

import {
  useMappingEditorStore,
  MappingPanelFilter,
  MappingPanelPosition,
  MappingPanelType,
  getSelectedStepNodeId,
  getSelectedFlowNodeId,
  getForInput,
} from '../MappingEditorStore';
import { getMappedOppositeY, MAPPING_ROW_HEIGHT } from '../MappingConnectors/MappingConnectorUtils';
import { ScrollContainer } from 'components/layout/ScrollContainer';
import { SkeletonTree } from 'loaders/SkeletonTree';
import { Checkbox } from 'components/inputs/Checkbox';

export interface IMappingColumnProps {
  title: string;
  filter: MappingPanelFilter;
  search: string;
  children?: React.ReactNode;
  position: MappingPanelPosition;
  side: MappingPanelType;
  className?: string;
  selectedNodeIds?: string[];
  hoverNodeIds?: string[];
}

/**
 * A component which renders one of the data trees, depending on config of editor,
 * e.g. input / output
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function MappingColumn({ children, className, filter, position, search, side, title }: IMappingColumnProps) {
  const setSearch = useMappingEditorStore(state => state.setSearch);
  const setFilter = useMappingEditorStore(state => state.setFilter);
  const toggleAlwaysShowEditors = useMappingEditorStore(state => state.toggleAlwaysShowEditors);
  const alwaysShowEditors = useMappingEditorStore(state => state.alwaysShowEditors);
  const forInput = useMappingEditorStore(getForInput);
  const mappings = useMappingEditorStore(state => state.mappings);
  const oppositeSelectedNode = useMappingEditorStore(
    useCallback(
      state => (side === MappingPanelType.FLOW ? getSelectedStepNodeId(state) : getSelectedFlowNodeId(state)),
      [side]
    )
  );

  const oppositeRowHeights = useMappingEditorStore(
    useCallback(state => (side === MappingPanelType.FLOW ? undefined : state.stepPanel.rowHeights), [side])
  );

  const scrollToY = useMemo(() => {
    // const oppositeScrollSelector = side === MappingPanelType.FLOW ? getStepScrollPosition : getFlowScrollPosition;
    // not confident useMemo is worth the overhead here. only mappings.find could be expensive in there, and there are lots of variables now.
    const oppositeY = getMappedOppositeY({
      mappings: mappings,
      selected: oppositeSelectedNode,
      leftToRight: forInput,
      side,
      rowHeights: oppositeRowHeights,
    });
    return oppositeY;
  }, [forInput, mappings, oppositeRowHeights, oppositeSelectedNode, side]);

  const isLoading = useMappingEditorStore(state =>
    side === MappingPanelType.FLOW ? state.flowPanel.isLoading : state.stepPanel.isLoading
  );
  const filterRef = useRef(null);

  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const setScroll = useMappingEditorStore(state => state.setScrollPosition);

  const noData = !isLoading && !React.Children.count(children);
  const onSearchChange = useCallback(
    (value: string) => setSearch(value, side),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onFilterClick = () => {
    setOpenFilter(true);
  };

  const onFilterSelect = (value: MappingPanelFilter) => {
    setFilter(value, side);
    setOpenFilter(false);
  };

  const onScroll = (e: React.UIEvent<HTMLElement>) => {
    // apparently scroll events are already throttled by requestAnimationFrame by the browser, so we don't need to do it here.
    setScroll((e.target as HTMLElement).scrollTop, side);
  };

  /** Let click events bubble to the ActionLinks, so events don't fire 2x */
  const clickPassThroughNoop = () => {};

  return (
    <div className={getClassName(position, className)}>
      <section className="dp-mapping-column__heading">
        <div className="dp-mapping-column__info">
          <h3 title={title} className="dp-mapping-column__title">
            {title}
          </h3>
        </div>
        <div className="dp-mapping-column__settings">
          <SearchField
            className="dp-mapping-column__search-field"
            value={search}
            onSearch={onSearchChange}
            onClear={() => onSearchChange('')}
            showHideBehavior
          />
          <IconButton
            ref={filterRef}
            className="dp-mapping-column__settings-button"
            iconClassName={getFilterIconClassName(openFilter)}
            iconSize="large"
            title="filter"
            onClick={onFilterClick}
          />
          {side === MappingPanelType.STEP && (
            <IconButton
              className="dp-mapping-column__constants-button"
              iconClassName={getConstantIconClassName(alwaysShowEditors)}
              iconSize="large"
              title={alwaysShowEditors ? 'Leave Constant Rows Collapsed' : 'Keep Constant Rows Expanded'}
              onClick={toggleAlwaysShowEditors}
            />
          )}
          <Callout
            isOpen={openFilter}
            onClose={() => setOpenFilter(false)}
            sourceRef={filterRef}
            placement="bottom"
            showArrow={true}
            flipBehavior="counterclockwise" //left, bottom, right, top ???
          >
            <div className="dp-mapping-column__filter-menu">
              <ActionLink
                label={getFilterLabel(MappingPanelFilter.MAPPED)}
                className="dp-mapping-column__filter-menu-item"
                value={MappingPanelFilter.MAPPED}
                onClick={onFilterSelect}
                isActive={filter === MappingPanelFilter.MAPPED}
                beforeLabel={
                  <Checkbox
                    onChange={clickPassThroughNoop}
                    checked={filter === MappingPanelFilter.MAPPED}
                    type="radio"
                  />
                }
              />
              <ActionLink
                label={getFilterLabel(MappingPanelFilter.UNMAPPED)}
                className="dp-mapping-column__filter-menu-item"
                value={MappingPanelFilter.UNMAPPED}
                onClick={onFilterSelect}
                isActive={filter === MappingPanelFilter.UNMAPPED}
                beforeLabel={
                  <Checkbox
                    onChange={clickPassThroughNoop}
                    checked={filter === MappingPanelFilter.UNMAPPED}
                    type="radio"
                  />
                }
              />
              <ActionLink
                label={getFilterLabel(MappingPanelFilter.ALL)}
                className="dp-mapping-column__filter-menu-item"
                value={MappingPanelFilter.ALL}
                onClick={onFilterSelect}
                isActive={filter === MappingPanelFilter.ALL}
                beforeLabel={
                  <Checkbox onChange={clickPassThroughNoop} checked={filter === MappingPanelFilter.ALL} type="radio" />
                }
              />
            </div>
          </Callout>
        </div>
      </section>
      <ScrollContainer
        className={getContentClassName(noData)}
        onScroll={onScroll}
        scrollToY={scrollToY}
        scrollToName={oppositeSelectedNode}
        scrollToYOffset={MAPPING_ROW_HEIGHT}
      >
        {children}
        {/* TODO something different than No results when loading, like a skeleton */}
        {isLoading && <SkeletonTree height={450} />}
        {noData && 'No Results'}
      </ScrollContainer>
    </div>
  );
}

function getContentClassName(noData: boolean) {
  return classNames('dp-mapping-column__content', {
    'dp-mapping-column__no-data flex-centering': noData,
  });
}
function getClassName(side: MappingPanelPosition, className?: string) {
  return classNames('dp-mapping-column', className, {
    'dp-mapping-column--left': side === MappingPanelPosition.LEFT,
    'dp-mapping-column--right': side === MappingPanelPosition.RIGHT,
  });
}

function getFilterIconClassName(open: boolean) {
  return classNames('dp-mapping-column__settings-icon', 'dp-mapping-column__settings-icon--filter', {
    'dp-mapping-column__settings-icon--open': open,
  });
}

function getConstantIconClassName(open: boolean) {
  return classNames('dp-mapping-column__settings-icon', {
    'dp-mapping-column__settings-icon--collapse': open,
    'dp-mapping-column__settings-icon--expand': !open,
  });
}

function getFilterLabel(filterValue: MappingPanelFilter) {
  switch (filterValue) {
    case MappingPanelFilter.MAPPED:
      return 'Show Mapped Only';
    case MappingPanelFilter.UNMAPPED:
      return 'Show Unmapped Only';
    case MappingPanelFilter.ALL:
      return 'Show All';
  }
}
